import { Code2, Smartphone, Globe, Database, Zap, CheckCircle2, ArrowRight, Sparkles, Layers, GitBranch, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";

const SoftwareDevelopment = () => {
  const solutions = [
    {
      icon: Globe,
      title: "Aplicações Web",
      description: "Sistemas web modernos e responsivos com as melhores tecnologias do mercado.",
      color: "from-blue-500/20 to-blue-600/20",
      features: [
        "React, Vue.js, Angular",
        "Node.js, Python, PHP",
        "Progressive Web Apps (PWA)",
        "Single Page Applications (SPA)"
      ]
    },
    {
      icon: Smartphone,
      title: "Aplicações Mobile",
      description: "Apps nativos e híbridos para iOS e Android com performance excepcional.",
      color: "from-green-500/20 to-green-600/20",
      features: [
        "React Native",
        "Flutter",
        "Apps nativos iOS/Android",
        "Integração com APIs"
      ]
    },
    {
      icon: Database,
      title: "Sistemas Sob Medida",
      description: "Soluções personalizadas para atender necessidades específicas do seu negócio.",
      color: "from-purple-500/20 to-purple-600/20",
      features: [
        "ERP e CRM customizados",
        "Sistemas de gestão",
        "Portais corporativos",
        "Intranets e extranets"
      ]
    },
    {
      icon: Server,
      title: "APIs e Integrações",
      description: "Conecte seus sistemas e automatize processos com APIs robustas.",
      color: "from-orange-500/20 to-orange-600/20",
      features: [
        "REST e GraphQL APIs",
        "Integração de sistemas",
        "Webhooks",
        "Microserviços"
      ]
    },
    {
      icon: Layers,
      title: "Arquitetura Escalável",
      description: "Projetos preparados para crescer junto com seu negócio.",
      color: "from-indigo-500/20 to-indigo-600/20",
      features: [
        "Cloud-native",
        "Containerização (Docker)",
        "CI/CD pipelines",
        "Alta disponibilidade"
      ]
    },
    {
      icon: GitBranch,
      title: "Manutenção e Suporte",
      description: "Acompanhamento contínuo para garantir o sucesso do seu projeto.",
      color: "from-pink-500/20 to-pink-600/20",
      features: [
        "Suporte técnico dedicado",
        "Atualizações regulares",
        "Monitoramento 24/7",
        "Correção de bugs"
      ]
    }
  ];

  const technologies = [
    { name: "React", category: "Frontend" },
    { name: "Node.js", category: "Backend" },
    { name: "TypeScript", category: "Language" },
    { name: "Python", category: "Backend" },
    { name: "PHP", category: "Backend" },
    { name: "Laravel", category: "Framework" },
    { name: "PostgreSQL", category: "Database" },
    { name: "MongoDB", category: "Database" },
    { name: "MySQL", category: "Database" },
    { name: "Docker", category: "DevOps" },
    { name: "React Native", category: "Mobile" },
    { name: "Next.js", category: "Framework" },
    { name: "Tailwind CSS", category: "Styling" },
    { name: "GraphQL", category: "API" },
    { name: "REST API", category: "API" }
  ];

  const process = [
    {
      step: "01",
      title: "Descoberta",
      description: "Entendemos suas necessidades e objetivos de negócio"
    },
    {
      step: "02",
      title: "Planejamento",
      description: "Definimos escopo, tecnologias e cronograma do projeto"
    },
    {
      step: "03",
      title: "Desenvolvimento",
      description: "Codificamos com qualidade usando metodologias ágeis"
    },
    {
      step: "04",
      title: "Testes",
      description: "Garantimos qualidade com testes rigorosos"
    },
    {
      step: "05",
      title: "Deploy",
      description: "Colocamos seu sistema em produção com segurança"
    },
    {
      step: "06",
      title: "Suporte",
      description: "Acompanhamos e evoluímos continuamente"
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] pointer-events-none" />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Voltar para Home
          </Link>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Desenvolvimento de Software</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 max-w-5xl mx-auto leading-tight">
            Transforme suas{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
              ideias em software
            </span>
          </h1>

          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Desenvolvemos soluções sob medida com tecnologias modernas para impulsionar seu negócio
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="gap-2 group">
              <Link to="/contato">
                <span className="flex items-center gap-2">
                  Iniciar Projeto <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contato">Ver Portfólio</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Nossas Soluções
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Do conceito ao deploy, desenvolvemos software de alta qualidade para web, mobile e desktop
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(99,159,255,0.2)] hover:scale-[1.02] group"
                >
                  <CardHeader>
                    <div className={`h-16 w-16 rounded-xl bg-gradient-to-br ${solution.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative`}>
                      <Icon className="h-8 w-8 text-primary relative z-10" />
                      <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <CardTitle className="text-xl text-foreground">{solution.title}</CardTitle>
                    <CardDescription className="text-base text-muted-foreground">
                      {solution.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 px-4 relative bg-card/30">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Tecnologias que Dominamos
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Trabalhamos com as melhores e mais modernas tecnologias do mercado
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-card/50 backdrop-blur-sm border border-border/50 rounded-full hover:border-primary/50 hover:bg-primary/10 transition-all duration-300 group"
              >
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {tech.name}
                </span>
                <span className="text-xs text-muted-foreground ml-2">
                  {tech.category}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Nosso Processo
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Metodologia ágil e transparente do início ao fim
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((item, index) => (
              <Card
                key={index}
                className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
              >
                <CardHeader>
                  <div className="text-5xl font-bold text-primary/20 mb-2 group-hover:text-primary/40 transition-colors">
                    {item.step}
                  </div>
                  <CardTitle className="text-xl text-foreground">{item.title}</CardTitle>
                  <CardDescription className="text-base text-muted-foreground">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <CardHeader className="text-center pb-6 relative z-10">
              <CardTitle className="text-3xl md:text-5xl mb-4 text-foreground">
                Pronto para começar seu projeto?
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg max-w-2xl mx-auto">
                Entre em contato e vamos transformar sua ideia em realidade
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center relative z-10">
              <Button size="lg" asChild className="gap-2 group/btn">
                <Link to="/contato">
                  <span className="flex items-center gap-2">
                    Solicitar Orçamento <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default SoftwareDevelopment;
